import React, { useEffect, useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Button, Table, Tag, Select, Tooltip, InputNumber, Input } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateBankAction, updateFormAction, updateOpenModalBankActionsHelp } from "../../../redux/banks/bankSlice";
import { usePutBankActionMutation } from "../../../redux/banks/bankAPI";
// COMPONENTS
import BankActionsTableActions from "./BankActionsTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
import TableColumnBankActionType from "../../components/table/TableColumnBankActionType";
import FormInputColorPicker from "../../components/form/FormInputColorPicker";
import TableColumnLiftType from "../../components/table/TableColumnLiftType";
import SelectMessages from "../../components/select/SelectMessages";
import SelectMessagesMultiple from "../../components/select/SelectMessagesMultiple";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// CUSTOM HOOKS
import useGetOneBank from "../../../customHooks/GET_requests/useGetOneBank";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// CONSTANTS
import { BLANK_BANK_ACTION } from "../../../constants/bank";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankActionInterface } from "../../../interfaces/Bank.interface";

export default function BankActionsTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Column, ColumnGroup } = Table;
	const { Option } = Select;
	const location = useLocation();
	const bankId = new URLSearchParams(location.search).get("idBank");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [bankActions, setBankActions] = useState<BankActionInterface[]>([]);
	const [action, setAction] = useState<BankActionInterface>({ ...BLANK_BANK_ACTION });
	const [totalRows, setTotalRows] = useState<number>(0);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerPutAction,
		{
			isSuccess: isSuccessPutAction,
			isLoading: isLoadingPutAction,
			isError: isErrorPutAction,
			error: errorPutAction,
			reset: resetPutAction,
		},
	] = usePutBankActionMutation();

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { bank, isLoadingGetOneBank } = useGetOneBank({ id: bankId ?? "" });

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { formAction } = useSelector((state: RootState) => state.bank);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeFormValue = (newValue: string | number, variableName: string) => {
		const copy: BankActionInterface = structuredClone(action);
		if (variableName === "timer" && typeof newValue === "number") {
			copy.timer = newValue;
		} else if (variableName === "toggle_timer" && typeof newValue === "number") {
			copy.toggle_timer = newValue;
		} else if (variableName === "color" && typeof newValue === "string") {
			copy.color = newValue;
		} else if (variableName === "toggle_color" && typeof newValue === "string") {
			copy.toggle_color = newValue;
		} else if (variableName === "label" && typeof newValue === "string") {
			copy.label = newValue;
		} else if (variableName === "toggle_label" && typeof newValue === "string") {
			copy.toggle_label = newValue;
		} else if (variableName === "button_type" && typeof newValue === "string") {
			copy.button_type = newValue as BankActionInterface["button_type"];
		}
		setAction(copy);
	};

	const onChangeMessage = (name: string, id: string) => {
		const copy: BankActionInterface = structuredClone(action);
		copy.message_id = id;
		setAction(copy);
	};

	const onChangeToggleMessage = (name: string, id: string) => {
		const copy: BankActionInterface = structuredClone(action);
		copy.toggle_message_id = id;
		setAction(copy);
	};

	const onChangeValidStates = (ids: string[]) => {
		const copy: BankActionInterface = structuredClone(action);
		copy.valid_states = ids;
		setAction(copy);
	};

	const onChangeToggleValidStates = (ids: string[]) => {
		const copy: BankActionInterface = structuredClone(action);
		copy.toggle_valid_states = ids;
		setAction(copy);
	};

	const onChangeButtonType = (
		newValue: "TOGGLE" | "TOGGLETIMER" | "HOLDBACK" | "TURNBACK" | "ENERGIZER_TURN_BACK" | "SWITCH"
	) => {
		const copy: BankActionInterface = structuredClone(action);
		copy.button_type = newValue;
		setAction(copy);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		if (formAction === "create") {
			const BODY = {
				token,
				id: bank.id,
				body: {
					actions: [{ ...action, bank_id: bank.id, timer2: 1, color2: "", action_id: "aae10657-f572-4b03-ae44-64eefbfff9e1", toggle_action_id: "14117d7b-b0fe-4e5e-b4d5-1f14a3cfec91" }],
					actions2delete: [],
				},
			};
			triggerPutAction(BODY);
		} else {
			const BODY = {
				token,
				id: bank.id,
				body: {
					actions: [{ ...action, bank_id: bank.id, timer2: 1, color2: "", action_id: action.message_id, toggle_action_id: action.toggle_message_id }],
					actions2delete: [],
				},
			};
			triggerPutAction(BODY);
		}
	};

	const resetForm = () => {
		resetPutAction();
		setAction({ ...BLANK_BANK_ACTION });
		dispatch(updateFormAction("create"));
		dispatch(updateBankAction({ ...BLANK_BANK_ACTION }));
	};

	const formIsValid = () => {
		if (
			action.timer <= 0 ||
			action.toggle_timer <= 0 ||
			action.message_id === "" ||
			action.toggle_color === "" ||
			action.color === "" ||
			action.toggle_message_id === ""
		) {
			return false;
		}
		return true;
	};

	const onClickInfoButton = () => {
		dispatch(updateOpenModalBankActionsHelp(true));
	};

	// ************************************************* */
	// USE EFFECT ************************************** */

	useEffect(() => {
		resetForm();
	}, [isSuccessPutAction, isErrorPutAction]);

	useEffect(() => {
		if (formAction === "create") {
			setAction({ ...BLANK_BANK_ACTION });
		}
	}, [formAction]);

	useEffect(() => {
		if (bank && bank.actions) {
			setBankActions(bank.actions);
			setTotalRows(bank.actions.length);
		} else {
			setBankActions([]);
			setTotalRows(0);
		}
	}, [bank]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div className='generalStyles__pageHeader' style={{ display: "flex", justifyContent: "space-between" }}>
				<div>
					<div className='generalStyles__flex'>
						<TableColumnLiftType liftType={bank.type} />
						<Tag color='orange'>{bank.name}</Tag>
						<FontAwesomeIcon
							className='generalStyles__info'
							style={{ fontSize: "18px", marginTop: "2px", cursor: "pointer" }}
							icon={faCircleInfo}
							onClick={onClickInfoButton}
						/>
					</div>
				</div>
			</div>
			<Table
				rowClassName={(record, index) => getTableRowClass(index, theme)}
				dataSource={bankActions}
				pagination={{
					defaultPageSize: 10,
					showSizeChanger: true,
					pageSizeOptions: ["10", "25", "50", "100"],
				}}
				scroll={{ x: 900 }}
				size='small'
				className='mt-3'
				loading={isLoadingPutAction || isLoadingGetOneBank}
				footer={() => (
					<div className='generalStyles__flexEnd'>
						<Tag>{totalRows} Rows</Tag>
					</div>
				)}
				rowKey={(record: BankActionInterface) => record.id}
			>
				{/** BUTTON TYPE */}
				<ColumnGroup title={t("general.buttonType")}>
					<Column
						title={
							<Select
								showSearch
								optionFilterProp='children'
								style={{ minWidth: "200px", width: "100%" }}
								allowClear={false}
								size='small'
								value={formAction === "create" ? action.button_type : "TOGGLE"}
								onChange={(
									e: "TOGGLE" | "TOGGLETIMER" | "HOLDBACK" | "TURNBACK" | "ENERGIZER_TURN_BACK" | "SWITCH"
								) => {
									onChangeButtonType(e);
								}}
							>
								{[
									{ id: "TOGGLE", name: "TOGGLE" },
									{ id: "TOGGLETIMER", name: "TOGGLE TIMER" },
									{ id: "HOLDBACK", name: "HOLDBACK" },
									{ id: "TURNBACK", name: "TURN BACK" },
									{ id: "ENERGIZER_TURN_BACK", name: "ENERGIZE TURN BACK" },
									{ id: "SWITCH", name: "SWITCH TOGGLE" },
								].map((item: { id: string; name: string }) => (
									<Option key={item.id} value={item.id}>
										{item.name}
									</Option>
								))}
							</Select>
						}
						dataIndex='button_type'
						key='button_type'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<Select
										showSearch
										optionFilterProp='children'
										style={{ minWidth: "200px", width: "100%" }}
										allowClear={false}
										size='small'
										value={action.button_type}
										onChange={(e) => {
											onChangeFormValue(e, "button_type");
										}}
									>
										{[
											{ id: "TOGGLE", name: "TOGGLE" },
											{ id: "TOGGLETIMER", name: "TOGGLE TIMER" },
											{ id: "HOLDBACK", name: "HOLDBACK" },
											{ id: "TURNBACK", name: "TURN BACK" },
											{ id: "ENERGIZER_TURN_BACK", name: "ENERGIZE TURN BACK" },
											{ id: "SWITCH", name: "SWITCH TOGGLE" },
										].map((item: { id: string; name: string }) => (
											<Option key={item.id} value={item.id}>
												{item.name}
											</Option>
										))}
									</Select>
								) : (
									<TableColumnBankActionType actionType={text || ""} />
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION ON */}
				<ColumnGroup title={t("general.action")}>
					<Column
						title={
							<SelectMessages
								inputValue={formAction === "create" ? action.message_id : ""}
								inputOnChange={onChangeMessage}
								disabledInput={formAction === "edit"}
								displaySelect
								messageType="CONTROL"

							/>
						}
						dataIndex='message_id'
						key='message_id'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<SelectMessages
										inputValue={action.message_id}
										inputOnChange={onChangeMessage}
										disabledInput={false}
										displaySelect
										messageType="CONTROL"

									/>
								) : (
									<SelectMessages
										inputValue={record.message_id}
										inputOnChange={() => { }}
										disabledInput
										displaySelect={false}
										messageType="CONTROL"

									/>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION ON TIMER */}
				<ColumnGroup title={t("general.timer")}>
					<Column
						title={
							<InputNumber
								min={1}
								value={formAction === "create" ? action.timer : 1}
								onChange={(e: any) => {
									onChangeFormValue(e, "timer");
								}}
								disabled={formAction === "edit" || action.button_type === "TOGGLE"}
								size='small'
								style={{ minWidth: "100px", width: "100%" }}
							/>
						}
						dataIndex='timer'
						key='timer'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<InputNumber
										min={0}
										value={action.timer}
										onChange={(e: any) => {
											onChangeFormValue(e, "timer");
										}}
										size='small'
										disabled={action.button_type === "TOGGLE"}
										style={{ minWidth: "100px", width: "100%" }}
									/>
								) : (
									<div> {text} (seconds)</div>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION ON COLOR */}
				<ColumnGroup title={t("general.buttonColor")}>
					<Column
						title={
							<FormInputColorPicker
								setColor={(newValue: string) => {
									onChangeFormValue(newValue, "color");
								}}
								color={action.color}
							/>
						}
						dataIndex='color'
						key='color'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<FormInputColorPicker
										setColor={(newValue: string) => {
											onChangeFormValue(newValue, "color");
										}}
										color={action.color}
									/>
								) : (
									<div className='generalStyles__flex'>
										<div
											style={{
												backgroundColor: `${text || "#fffff"}`,
												height: "20px",
												width: "20px",
												marginLeft: "25px",
												borderRadius: "100px",
											}}
										/>
										<div className='generalStyles__mlFix'>{text}</div>
									</div>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION LABEL */}
				<ColumnGroup title={t("general.label")}>
					<Column
						title={
							<Input
								size='small'
								style={{ minWidth: "150px", width: "100%" }}
								value={formAction === "create" ? action.label : ""}
								onChange={(e) => {
									onChangeFormValue(e.target.value, "label");
								}}
								disabled={formAction === "edit"}
								placeholder="Enter label"
							/>
						}
						dataIndex='label'
						key='label'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<Input
										size='small'
										style={{ minWidth: "150px", width: "100%" }}
										value={action.label}
										onChange={(e) => {
											onChangeFormValue(e.target.value, "label");
										}}
										placeholder="Enter label"
									/>
								) : (
									<p>{text || "-"}</p>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** VALID STATES */}
				<ColumnGroup title='Valid States'>
					<Column
						title={
							<SelectMessagesMultiple
								inputValue={formAction === "create" ? action.valid_states : []}
								inputOnChange={onChangeValidStates}
								disabledInput={formAction === "edit"}
								displaySelect
							/>
						}
						dataIndex='valid_states'
						key='valid_states'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<SelectMessagesMultiple
										inputValue={action.valid_states}
										inputOnChange={onChangeValidStates}
										disabledInput={false}
										displaySelect
									/>
								) : (
									<SelectMessagesMultiple
										inputValue={record.valid_states}
										inputOnChange={() => { }}
										disabledInput
										displaySelect={false}
									/>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION OFF */}
				<ColumnGroup title='Toggle Action'>
					<Column
						title={
							<SelectMessages
								inputValue={formAction === "create" ? action.toggle_message_id : ""}
								inputOnChange={onChangeToggleMessage}
								disabledInput={formAction === "edit"}
								displaySelect
								messageType="CONTROL"
							/>
						}
						dataIndex='toggle_message_id'
						key='toggle_message_id'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<SelectMessages
										inputValue={action.toggle_message_id}
										inputOnChange={onChangeToggleMessage}
										disabledInput={false}
										displaySelect
										messageType="CONTROL"

									/>
								) : (
									<SelectMessages
										inputValue={record.toggle_message_id}
										inputOnChange={() => { }}
										disabledInput
										displaySelect={false}
										messageType="CONTROL"

									/>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION OFF TIMER */}
				<ColumnGroup title='Toggle Timer'>
					<Column
						title={
							<InputNumber
								min={1}
								value={formAction === "create" ? action.toggle_timer : 1}
								onChange={(e: any) => {
									onChangeFormValue(e, "toggle_timer");
								}}
								disabled={
									formAction === "edit" ||
									action.button_type === "TOGGLE" ||
									action.button_type === "HOLDBACK" ||
									action.button_type === "TURNBACK" ||
									action.button_type === "ENERGIZER_TURN_BACK"
								}
								size='small'
								style={{ minWidth: "100px", width: "100%" }}
							/>
						}
						dataIndex='toggle_timer'
						key='toggle_timer'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<InputNumber
										min={0}
										value={action.toggle_timer}
										onChange={(e: any) => {
											onChangeFormValue(e, "toggle_timer");
										}}
										size='small'
										disabled={
											action.button_type === "TOGGLE" ||
											action.button_type === "HOLDBACK" ||
											action.button_type === "TURNBACK" ||
											action.button_type === "ENERGIZER_TURN_BACK"
										}
										style={{ minWidth: "100px", width: "100%" }}
									/>
								) : (
									<div> {text} (seconds)</div>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** ACTION OFF COLOR */}
				<ColumnGroup title='Toggle Button Color'>
					<Column
						title={
							<FormInputColorPicker
								setColor={(newValue: string) => {
									onChangeFormValue(newValue, "toggle_color");
								}}
								color={action.toggle_color}
							/>
						}
						dataIndex='toggle_color'
						key='toggle_color'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<FormInputColorPicker
										setColor={(newValue: string) => {
											onChangeFormValue(newValue, "toggle_color");
										}}
										color={action.toggle_color}
									/>
								) : (
									<div className='generalStyles__flex'>
										<div
											style={{
												backgroundColor: `${text || "#fffff"}`,
												height: "20px",
												width: "20px",
												marginLeft: "25px",
												borderRadius: "100px",
											}}
										/>
										<div className='generalStyles__mlFix'>{text}</div>
									</div>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** TOGGLE LABEL */}
				<ColumnGroup title='Toggle Label'>
					<Column
						title={
							<Input
								size='small'
								style={{ minWidth: "150px", width: "100%" }}
								value={formAction === "create" ? action.toggle_label : ""}
								onChange={(e) => {
									onChangeFormValue(e.target.value, "toggle_label");
								}}
								disabled={formAction === "edit"}
								placeholder="Enter toggle label"
							/>
						}
						dataIndex='toggle_label'
						key='toggle_label'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<Input
										size='small'
										style={{ minWidth: "150px", width: "100%" }}
										value={action.toggle_label}
										onChange={(e) => {
											onChangeFormValue(e.target.value, "toggle_label");
										}}
										placeholder="Enter toggle label"
									/>
								) : (
									<p>{text || "-"}</p>
								)}
							</div>
						)}
					/>
				</ColumnGroup>
				{/** TOGGLE VALID STATES */}
				<ColumnGroup title='Toggle Valid States'>
					<Column
						title={
							<SelectMessagesMultiple
								inputValue={formAction === "create" ? action.toggle_valid_states : []}
								inputOnChange={onChangeToggleValidStates}
								disabledInput={formAction === "edit"}
								displaySelect
							/>
						}
						dataIndex='toggle_valid_states'
						key='toggle_valid_states'
						render={(text, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<SelectMessagesMultiple
										inputValue={action.toggle_valid_states}
										inputOnChange={onChangeToggleValidStates}
										disabledInput={false}
										displaySelect
									/>
								) : (
									<SelectMessagesMultiple
										inputValue={record.toggle_valid_states}
										inputOnChange={() => { }}
										disabledInput
										displaySelect={false}
									/>
								)}
							</div>
						)}
					/>
				</ColumnGroup>

				{/** ACTIONS */}
				<ColumnGroup align='center' title={t("edgeManagement.banks.actions")}>
					<Column
						align='center'
						title={
							<div className='generalStyles__flexCenter'>
								<div>
									<Tooltip title={t("general.save")}>
										<Button
											className={formAction === "create" && formIsValid() ? "buttonStyle__10" : ""}
											disabled={!formIsValid() || formAction === "edit"}
											onClick={onClickSave}
											icon={<FontAwesomeIcon icon={faSave} />}
										/>
									</Tooltip>
								</div>
								<div className='generalStyles__mlFix'>
									<Tooltip title={t("general.clear")}>
										<Button
											className={formAction === "create" ? "buttonStyle__14" : ""}
											onClick={resetForm}
											disabled={formAction === "edit"}
											icon={<FontAwesomeIcon icon={faX} />}
										/>
									</Tooltip>
								</div>
							</div>
						}
						key='action'
						render={(_: any, record: BankActionInterface) => (
							<div>
								{formAction === "edit" && record.id === action.id ? (
									<div className='generalStyles__flexCenter'>
										<div>
											<Tooltip title={t("general.save")}>
												<Button
													className='buttonStyle__10'
													disabled={!formIsValid()}
													onClick={onClickSave}
													icon={<FontAwesomeIcon icon={faSave} />}
												/>
											</Tooltip>
										</div>
										<div className='generalStyles__mlFix'>
											<Tooltip title={t("general.cancel")}>
												<Button className='buttonStyle__14' onClick={resetForm} icon={<FontAwesomeIcon icon={faX} />} />
											</Tooltip>
										</div>
									</div>
								) : (
									<BankActionsTableActions selectedItem={record} />
								)}
							</div>
						)}
					/>
				</ColumnGroup>
			</Table>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPutAction}
				isSuccess={isSuccessPutAction}
				requestType='PUT'
				error={errorPutAction}
				name='Action'
			/>
		</div>
	);
}
